import React from 'react';
import { Tooltip } from 'antd';

import { formatWholeNumber, formatCurrency2SigFig } from '../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// BasketItemTable — per-item breakdown on the V2 Basket Items tab.
//
// Rows: { name, quantity, total, color }. Sortable on Name / Quantity /
// Total; right-aligned numerics; totals summary at the bottom. Mobile card
// renders name + 2 metric tiles (Quantity + Total).
// =============================================================================

const numericSort = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const nameSort = (rowA, rowB) => {
  const a = rowA.original.name || '';
  const b = rowB.original.name || '';
  return String(a).localeCompare(String(b));
};

const renderItemName = (text) => {
  if (!text) return <div />;
  if (text.length > 45) {
    return (
      <Tooltip overlayClassName="mjl-ds" title={text}>
        <span className="campaign-overview__creatives-name">
          {text.slice(0, 46)}…
        </span>
      </Tooltip>
    );
  }
  return <span className="campaign-overview__creatives-name">{text}</span>;
};

const BasketItemTable = ({ basketItemData }) => {
  const rows = basketItemData || [];

  const columns = [
    {
      id: 'color',
      accessorKey: 'color',
      header: '',
      sortable: false,
      meta: { align: 'center', width: 24 },
      cell: ({ getValue }) => (
        <span
          className="campaign-overview__creatives-dot"
          style={{ backgroundColor: getValue() }}
          aria-hidden="true"
        />
      ),
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      sortable: true,
      sortingFn: nameSort,
      meta: { align: 'left', width: 280 },
      cell: ({ getValue }) => renderItemName(getValue()),
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: 'Quantity',
      sortable: true,
      sortingFn: numericSort('quantity'),
      meta: { align: 'right', width: 120 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? formatWholeNumber(v) : '-';
      },
    },
    {
      id: 'total',
      accessorKey: 'total',
      header: 'Total',
      sortable: true,
      sortingFn: numericSort('total'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? formatCurrency2SigFig(v) : '-';
      },
    },
  ];

  const totalQuantity = rows.reduce(
    (sum, d) => sum + (parseFloat(d.quantity) || 0),
    0
  );
  const totalSum = rows.reduce(
    (sum, d) => sum + (parseFloat(d.total) || 0),
    0
  );

  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span
            className="campaign-overview__creatives-dot"
            style={{ backgroundColor: record.color }}
            aria-hidden="true"
          />
          {renderItemName(record.name)}
        </div>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Quantity</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.quantity ? formatWholeNumber(record.quantity) : '-'}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Total</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {record.total ? formatCurrency2SigFig(record.total) : '-'}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      summaryRow={{
        labelColumnId: 'name',
        label: 'Totals + Averages',
        cells: {
          quantity: formatWholeNumber(totalQuantity),
          total: formatCurrency2SigFig(totalSum),
        },
      }}
      emptyState="No basket items match the current filter."
    />
  );
};

export default BasketItemTable;
